import { useEffect, useEffectEvent, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { MfaCodeInput } from '@components/Mfa/MfaCodeInput/MfaCodeInput';
import {
  confirmMfaEnrollment,
  enrollMfa,
  MfaInvalidCodeError,
  MfaPasswordRejectedError,
  MfaRateLimitedError,
  type MfaEnrollConfirmResult,
} from '@apis/mfa';
import type { IMfaEnrollment } from '@interfaces/IMfa';
import { MFA_CODE_LENGTH, MFA_PENDING_TTL_MS } from '@constants/mfa';
import { copyToClipboard } from '@utils/clipboard';
import { useTransientFlag } from '@hooks/useTransientFlag';
import styles from './styles.module.css';

interface IMfaEnrollmentProps {
  /**
   * Which credential authorises the enrollment, which also decides what
   * `enroll/confirm` answers with:
   *  - `pending`  — forced, mid-login on `dorca_pending`; confirm returns
   *    `{user}` and the session is promoted. The pending cookie can age out,
   *    so this context arms the TTL timer.
   *  - `settings` — voluntary, on a full session; confirm returns
   *    `{enabled: true}` and there is nothing to expire.
   */
  context: 'pending' | 'settings';
  onConfirmed: (result: MfaEnrollConfirmResult) => void | Promise<void>;
  /** Only meaningful for `context: 'pending'`. */
  onPendingExpired?: () => void;
  onCancel?: () => void;
}

type Step = 'password' | 'setup' | 'confirm';

/**
 * Read the shared secret straight out of the `otpauth://` URI so the manual
 * fallback ("can't scan? type this") costs no extra field on the contract.
 */
function secretFromTotpUri(uri: string): string | null {
  try {
    return new URL(uri).searchParams.get('secret');
  } catch {
    return null;
  }
}

/** Chunk the base32 secret into 4s — it gets typed by hand on a phone. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * TOTP enrollment, used unchanged in both places it's needed: forced after a
 * login that demands MFA, and voluntarily from the security settings. The only
 * difference is which cookie authorises it and what the confirm call answers —
 * both carried by `context`.
 *
 * Flow: password re-prompt → QR + manual secret + one-time backup codes behind
 * an "I saved them" gate → 6-digit confirm. The backup codes are shown exactly
 * once (the server keeps only hashes), which is why the gate exists at all:
 * it's the last moment they're recoverable.
 */
export function MfaEnrollment({ context, onConfirmed, onPendingExpired, onCancel }: IMfaEnrollmentProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('password');
  const [password, setPassword] = useState('');
  const [enrollment, setEnrollment] = useState<IMfaEnrollment | null>(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [copied, flashCopied] = useTransientFlag<'secret' | 'codes'>();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const expire = useEffectEvent(() => onPendingExpired?.());
  useEffect(() => {
    if (context !== 'pending') return;
    const timer = window.setTimeout(expire, MFA_PENDING_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [context]);

  async function onSubmitPassword(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const result = await enrollMfa(password);
      setEnrollment(result);
      // Don't keep the password in state a moment longer than the one call
      // that needed it.
      setPassword('');
      setStep('setup');
    } catch (error) {
      setErr(
        error instanceof MfaPasswordRejectedError
          ? t('auth.mfa.enroll.wrongPassword')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.enroll.genericError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCode(rawCode: string) {
    const value = rawCode.trim();
    if (value.length !== MFA_CODE_LENGTH || submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const result = await confirmMfaEnrollment(value);
      await onConfirmed(result);
    } catch (error) {
      // Same neutral framing as the challenge — never distinguish a wrong code
      // from a stale one. 429 is the one documented exception.
      setErr(
        error instanceof MfaInvalidCodeError
          ? t('auth.mfa.enroll.invalidCode')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.enroll.genericError'),
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  const secret = enrollment ? secretFromTotpUri(enrollment.totpUri) : null;
  // Backup codes ride along only on a first-factor enrolment. Adding an
  // authenticator to an account that already has a factor reuses the existing
  // secret and returns none — so the "save your codes" gate must not appear (and
  // must not block the confirm step) when there is nothing to save.
  const backupCodes = enrollment?.backupCodes ?? [];
  const hasBackupCodes = backupCodes.length > 0;

  return (
    <div className={styles.root}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>{t('auth.mfa.eyebrow')}</span>
        <h1 className={styles.title}>{t('auth.mfa.enroll.title')}</h1>
        <p className={styles.subtitle}>
          {t(context === 'pending' ? 'auth.mfa.enroll.subtitleForced' : 'auth.mfa.enroll.subtitle')}
        </p>
      </div>

      {step === 'password' ? (
        <form onSubmit={onSubmitPassword} className={styles.form} noValidate>
          <Field
            label={t('auth.mfa.enroll.passwordLabel')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={t('auth.mfa.enroll.passwordHint')}
            autoComplete="current-password"
            autoFocus
            required
          />
          {err ? (
            <div className={styles.error} role="alert">
              {err}
            </div>
          ) : null}
          <Button type="submit" variant="primary" size="md" block disabled={submitting || password.length === 0}>
            {submitting ? t('auth.mfa.enroll.starting') : t('auth.mfa.enroll.start')}
          </Button>
        </form>
      ) : null}

      {step === 'setup' && enrollment ? (
        <div className={styles.setup}>
          <ol className={styles.steps}>
            <li className={styles.step}>
              <span className={styles.stepTitle}>{t('auth.mfa.enroll.scanTitle')}</span>
              <p className={styles.stepBody}>{t('auth.mfa.enroll.scanBody')}</p>
              <div className={styles.qrFrame}>
                {/* Rendered from the URI client-side — the secret never leaves
                    the page as an image request. */}
                <QRCodeSVG
                  value={enrollment.totpUri}
                  size={168}
                  level="M"
                  marginSize={2}
                  title={t('auth.mfa.enroll.qrAlt')}
                />
              </div>
              {secret ? (
                <div className={styles.secretBlock}>
                  <span className={styles.secretLabel}>{t('auth.mfa.enroll.manualLabel')}</span>
                  <code className={styles.secret}>{groupSecret(secret)}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void copyToClipboard(secret).then((ok) => ok && flashCopied('secret'))}
                  >
                    {copied === 'secret' ? t('auth.mfa.enroll.copied') : t('auth.mfa.enroll.copy')}
                  </Button>
                </div>
              ) : null}
            </li>

            {hasBackupCodes ? (
              <li className={styles.step}>
                <span className={styles.stepTitle}>{t('auth.mfa.enroll.backupTitle')}</span>
                <p className={styles.stepBody}>{t('auth.mfa.enroll.backupBody')}</p>
                <ul className={styles.codeGrid} aria-label={t('auth.mfa.enroll.backupTitle')}>
                  {backupCodes.map((c) => (
                    <li key={c} className={styles.backupCode}>
                      {c}
                    </li>
                  ))}
                </ul>
                <div className={styles.backupActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      void copyToClipboard(backupCodes.join('\n')).then((ok) => ok && flashCopied('codes'))
                    }
                  >
                    {copied === 'codes' ? t('auth.mfa.enroll.copied') : t('auth.mfa.enroll.copyCodes')}
                  </Button>
                </div>
                <label className={styles.gate}>
                  <input type="checkbox" checked={savedCodes} onChange={(e) => setSavedCodes(e.target.checked)} />
                  <span>{t('auth.mfa.enroll.savedConfirm')}</span>
                </label>
              </li>
            ) : null}
          </ol>

          <Button
            variant="primary"
            size="md"
            block
            disabled={hasBackupCodes && !savedCodes}
            onClick={() => {
              setErr(null);
              setStep('confirm');
            }}
          >
            {t('auth.mfa.enroll.continue')}
          </Button>
        </div>
      ) : null}

      {step === 'confirm' ? (
        <form
          className={styles.form}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submitCode(code);
          }}
        >
          <p className={styles.confirmBody}>{t('auth.mfa.enroll.confirmBody')}</p>
          <MfaCodeInput
            label={t('auth.mfa.enroll.codeLabel')}
            value={code}
            onChange={setCode}
            onComplete={(full) => void submitCode(full)}
            disabled={submitting}
            autoFocus
          />
          {err ? (
            <div className={styles.error} role="alert">
              {err}
            </div>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            size="md"
            block
            disabled={submitting || code.length !== MFA_CODE_LENGTH}
          >
            {submitting ? t('auth.mfa.enroll.confirming') : t('auth.mfa.enroll.confirm')}
          </Button>
          <button type="button" className={styles.linkButton} onClick={() => setStep('setup')}>
            {t('auth.mfa.enroll.backToCodes')}
          </button>
        </form>
      ) : null}

      {onCancel ? (
        <div className={styles.footerRow}>
          <button type="button" className={styles.linkButton} onClick={onCancel}>
            {t(context === 'pending' ? 'auth.mfa.enroll.cancelForced' : 'auth.mfa.enroll.cancel')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
